import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { sanitizeText, signInSchema, signUpSchema, type SignUpData } from "@/lib/validations";

export type DisplayNameSuggestion = { name: string; available: boolean; avatarUrl?: string };

type UseAuthFormOptions = {
  withAvatarPreview?: boolean;
  signUpRedirectUrl?: string;
};

type AuthSubmitStatus = "success" | "existing-user" | "unverified" | "validation-error" | "error";

export type AuthSubmitResult = {
  status: AuthSubmitStatus;
  flow: "login" | "signup";
};

const AVATAR_GENERATION_DEBOUNCE = 1000;

export const useAuthForm = ({ withAvatarPreview = false, signUpRedirectUrl }: UseAuthFormOptions = {}) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [turnstileToken, setTurnstileToken] = useState<string>("");
  const [turnstileWidgetId, setTurnstileWidgetId] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<DisplayNameSuggestion[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [suggestionTimestamps, setSuggestionTimestamps] = useState<number[]>([]);
  const [inspirationText, setInspirationText] = useState("");
  const [usedAISuggestion, setUsedAISuggestion] = useState(false);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState("");
  const [currentAvatarUrl, setCurrentAvatarUrl] = useState<string | null>(null);
  const [generatingAvatar, setGeneratingAvatar] = useState(false);
  const avatarGenerationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!withAvatarPreview) {
      return;
    }

    if (!isLogin && displayName.trim() && displayName.trim().length >= 3) {
      if (avatarGenerationTimeoutRef.current) {
        clearTimeout(avatarGenerationTimeoutRef.current);
      }

      avatarGenerationTimeoutRef.current = setTimeout(async () => {
        const avatarUrl = await generateAvatarPreview(displayName.trim(), inspirationText || undefined);
        if (avatarUrl) {
          setCurrentAvatarUrl(avatarUrl);
        }
      }, AVATAR_GENERATION_DEBOUNCE);
    }

    return () => {
      if (avatarGenerationTimeoutRef.current) {
        clearTimeout(avatarGenerationTimeoutRef.current);
      }
    };
  }, [displayName, inspirationText, isLogin, withAvatarPreview]);

  const generateAvatarPreview = async (name: string, inspiration?: string) => {
    if (!name.trim()) return null;

    setGeneratingAvatar(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-profile-avatar", {
        body: {
          user_id: null,
          inspiration_text: inspiration || null,
          display_name: name.trim(),
          preview_mode: true,
        },
      });

      if (error) throw error;
      return data?.avatar_url || null;
    } catch (error) {
      console.error("Avatar preview generation failed:", error);
      return null;
    } finally {
      setGeneratingAvatar(false);
    }
  };

  const handleAIError = (errorCode: string, message?: string, remainingSeconds?: number) => {
    const errorMessages: Record<string, string> = {
      FEATURE_DISABLED: "AI suggestions are temporarily unavailable. Please choose a display name manually.",
      DAILY_LIMIT_REACHED: "Daily AI suggestion limit reached. Please try again tomorrow or choose manually.",
      SESSION_LIMIT_REACHED:
        "You've reached the maximum AI suggestions for this session. Please choose a display name manually.",
      RATE_LIMIT: message || "Please wait before requesting more suggestions.",
      CREDITS_EXHAUSTED:
        "AI suggestions are temporarily unavailable due to high demand. Please choose a display name manually.",
      SESSION_NOT_FOUND: "Session not initialized. Please refresh the page.",
      INVALID_SESSION: "Invalid session. Please refresh the page.",
      LOVABLE_RATE_LIMIT: "AI service is busy. Please try again in a moment.",
      INSPIRATION_REQUIRED: "Please enter inspiration keywords.",
      INSUFFICIENT_WORDS: "Please provide at least 5 words for inspiration.",
      AI_NOT_CONFIGURED: "AI service is not configured.",
      UNKNOWN_ERROR: "Failed to get suggestions. Please try again.",
    };

    const displayMessage = errorMessages[errorCode] || message || errorMessages["UNKNOWN_ERROR"];

    if (errorCode === "RATE_LIMIT" && remainingSeconds) {
      toast.error(`Please wait ${remainingSeconds} seconds before requesting more suggestions.`);
    } else {
      toast.error(displayMessage);
    }

    console.error("AI Error:", errorCode, message);
  };

  const handleGetSuggestions = async () => {
    if (!displayName.trim()) {
      toast.error("Please enter inspiration keywords in the Display Name field");
      return;
    }

    const words = displayName.trim().split(/\s+/);
    if (words.length < 5) {
      toast.error("Please enter at least 5 words (e.g., colors, activities, interests)");
      return;
    }

    const sessionKey = localStorage.getItem("session_key");
    if (!sessionKey) {
      toast.error("Session not initialized. Please refresh the page.");
      return;
    }

    setInspirationText(displayName.trim());
    setLoadingSuggestions(true);
    setSuggestions([]);

    try {
      const { data, error } = await supabase.functions.invoke("suggest-display-name", {
        body: {
          inspiration: displayName.trim(),
          session_key: sessionKey,
        },
      });

      if (error || data?.error) {
        let errorCode = "UNKNOWN_ERROR";
        let message = undefined;
        let remainingSeconds = undefined;

        if (error) {
          try {
            const errorContext = (error as any)?.context;
            if (errorContext) {
              errorCode = errorContext.error || "UNKNOWN_ERROR";
              message = errorContext.message;
              remainingSeconds = errorContext.remainingSeconds;
            }
          } catch (parseError) {
            console.error("Error parsing error context:", parseError);
          }
        } else if (data?.error) {
          errorCode = data.error;
          message = data.message;
          remainingSeconds = data.remainingSeconds;
        }

        handleAIError(errorCode, message, remainingSeconds);
        return;
      }

      if (data?.suggestions && data.suggestions.length > 0) {
        if (withAvatarPreview) {
          const suggestionsWithAvatars = await Promise.all(
            data.suggestions.map(async (suggestion: DisplayNameSuggestion) => {
              const avatarUrl = await generateAvatarPreview(suggestion.name, displayName.trim());
              return { ...suggestion, avatarUrl };
            }),
          );

          setSuggestions(suggestionsWithAvatars);

          if (suggestionsWithAvatars[0]?.avatarUrl) {
            setCurrentAvatarUrl(suggestionsWithAvatars[0].avatarUrl);
          }
        } else {
          setSuggestions(data.suggestions);
        }

        toast.success("Here are some suggestions!");
      } else {
        toast.error("No suggestions generated. Try different keywords.");
      }
    } catch (error) {
      console.error("Error calling suggest-display-name:", error);
      toast.error("Failed to get suggestions. Please try again.");
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const selectSuggestion = (suggestion: DisplayNameSuggestion) => {
    setDisplayName(suggestion.name);
    setUsedAISuggestion(true);
    setSuggestions([]);
    setErrors((prev) => ({ ...prev, displayName: "" }));
    if (suggestion.avatarUrl) {
      setCurrentAvatarUrl(suggestion.avatarUrl);
    }
    toast.success(`Display name set to: ${suggestion.name}`);
  };

  const handleTurnstileError = () => {
    toast.error("CAPTCHA verification failed. Please try again.");
    setTurnstileToken("");
  };

  const handleAuthSubmit = async (): Promise<AuthSubmitResult> => {
    setLoading(true);
    setErrors({});

    try {
      const formData = {
        email: sanitizeText(email),
        password,
        ...(isLogin ? {} : { displayName: sanitizeText(displayName) }),
      };

      const schema = isLogin ? signInSchema : signUpSchema;
      const validation = schema.safeParse(formData);

      if (!validation.success) {
        const fieldErrors: Record<string, string> = {};
        validation.error.issues.forEach((error) => {
          if (error.path[0]) {
            fieldErrors[error.path[0] as string] = error.message;
          }
        });
        setErrors(fieldErrors);
        return { status: "validation-error", flow: isLogin ? "login" : "signup" };
      }

      if (isLogin) {
        const { data: signInData, error } = await supabase.auth.signInWithPassword({
          email: validation.data.email,
          password: validation.data.password,
        });
        if (error) throw error;

        if (signInData.user && !signInData.user.email_confirmed_at) {
          console.warn("⚠️ User attempted login with unverified email");
          await supabase.auth.signOut();
          setUnverifiedEmail(validation.data.email);
          setShowVerificationDialog(true);
          return { status: "unverified", flow: "login" };
        }

        toast.success("Signed in successfully!");
        return { status: "success", flow: "login" };
      }

      if (!turnstileToken) {
        toast.error("Please complete the CAPTCHA verification");
        return { status: "error", flow: "signup" };
      }

      const { data: verifyData, error: verifyError } = await supabase.functions.invoke("verify-turnstile", {
        body: { token: turnstileToken },
      });

      if (verifyError || !verifyData?.success) {
        toast.error("CAPTCHA verification failed. Please try again.");
        setTurnstileToken("");
        if (window.turnstile) {
          window.turnstile.reset();
        }
        return { status: "error", flow: "signup" };
      }

      const { data: existingUserCheck } = await supabase.functions.invoke("send-signup-notification", {
        body: {
          email: validation.data.email,
          checkOnly: true,
        },
      });

      if (existingUserCheck?.userExists) {
        toast.success(
          "Account created! Please check your email and click the verification link to activate your account.",
        );
        return { status: "existing-user", flow: "signup" };
      }

      const redirectUrl = signUpRedirectUrl ?? `${window.location.origin}${window.location.pathname}`;
      const displayNameValue = (validation.data as SignUpData).displayName || "";
      const { error } = await supabase.auth.signUp({
        email: validation.data.email,
        password: validation.data.password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            display_name: displayNameValue,
            inspiration_text: usedAISuggestion && inspirationText ? inspirationText : null,
          },
        },
      });

      if (error) throw error;

      toast.success(
        "Account created! Please check your email and click the verification link to activate your account.",
      );
      return { status: "success", flow: "signup" };
    } catch (error: any) {
      const errorMessage = error.message?.toLowerCase() || "";

      if (errorMessage.includes("email not confirmed") || errorMessage.includes("email_not_confirmed")) {
        setUnverifiedEmail(email);
        setShowVerificationDialog(true);
        return { status: "unverified", flow: isLogin ? "login" : "signup" };
      }

      if (errorMessage.includes("rate limit") || errorMessage.includes("email rate limit")) {
        toast.error(
          "Too many signup attempts. Please try again in about an hour, or try using a different email address.",
        );
      } else {
        toast.error(error.message);
      }
      return { status: "error", flow: isLogin ? "login" : "signup" };
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLink = async () => {
    if (!email) {
      toast.error("Please enter your email address");
      return false;
    }

    setLoading(true);
    setErrors({});

    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: sanitizeText(email),
        options: {
          emailRedirectTo: `${window.location.origin}${window.location.pathname}`,
        },
      });

      if (error) throw error;
      toast.success("Check your email for the magic link! Clicking it will verify your email and sign you in.");
      return true;
    } catch (error: any) {
      toast.error(error.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setIsLogin(true);
    setEmail("");
    setPassword("");
    setDisplayName("");
    setErrors({});
    setTurnstileToken("");
    setTurnstileWidgetId(null);
    setSuggestions([]);
    setLoadingSuggestions(false);
    setSuggestionTimestamps([]);
    setInspirationText("");
    setUsedAISuggestion(false);
    setShowVerificationDialog(false);
    setUnverifiedEmail("");
    setCurrentAvatarUrl(null);
    setGeneratingAvatar(false);
  };

  return {
    isLogin,
    setIsLogin,
    email,
    setEmail,
    password,
    setPassword,
    displayName,
    setDisplayName,
    loading,
    errors,
    setErrors,
    turnstileToken,
    setTurnstileToken,
    turnstileWidgetId,
    setTurnstileWidgetId,
    suggestions,
    loadingSuggestions,
    suggestionTimestamps,
    handleGetSuggestions,
    selectSuggestion,
    inspirationText,
    usedAISuggestion,
    showVerificationDialog,
    setShowVerificationDialog,
    unverifiedEmail,
    currentAvatarUrl,
    generatingAvatar,
    handleTurnstileError,
    handleAuthSubmit,
    handleMagicLink,
    resetForm,
  };
};
